import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NativeAudio } from '@capacitor-community/native-audio';

export type SuccessSound = 'medium' | 'light';

type SoundEffectId = 'mediumSuccess' | 'lightSuccess' | 'error';

interface SoundEffectDef {
  assetId: string;
  fileName: string;
  webPath: string;
  volume: number;
}

const SOUND_EFFECTS: Record<SoundEffectId, SoundEffectDef> = {
  mediumSuccess: {
    assetId: 'alert-success-medium',
    fileName: 'success-short.mp3',
    webPath: 'assets/sounds/success-short.mp3',
    volume: 0.6,
  },
  lightSuccess: {
    assetId: 'alert-success-light',
    fileName: 'light-success.mp3',
    webPath: 'assets/sounds/light-success.mp3',
    volume: 0.6,
  },
  error: {
    assetId: 'alert-error',
    fileName: 'error.mp3',
    webPath: 'assets/sounds/error.mp3',
    volume: 0.6,
  },
};

const SUCCESS_SOUND_TO_EFFECT: Record<SuccessSound, SoundEffectId> = {
  medium: 'mediumSuccess',
  light: 'lightSuccess',
};

@Injectable({ providedIn: 'root' })
export class SoundEffectsService {
  #ready: Promise<void> | null = null;
  #webAudio = new Map<SoundEffectId, HTMLAudioElement>();

  init(): void {
    this.#ready ??= this.#preload().catch(() => undefined);
  }

  mediumSuccess(): void {
    this.#play('mediumSuccess');
  }

  lightSuccess(): void {
    this.#play('lightSuccess');
  }

  success(sound: SuccessSound = 'medium'): void {
    this.#play(SUCCESS_SOUND_TO_EFFECT[sound]);
  }

  error(): void {
    this.#play('error');
  }

  #play(id: SoundEffectId): void {
    void this.#ensureReady()
      .then(() => this.#playEffect(id))
      .catch(() => undefined);
  }

  async #ensureReady(): Promise<void> {
    this.init();
    await this.#ready;
  }

  async #preload(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await NativeAudio.configure({ fade: false, focus: false });
      await Promise.all(
        (Object.keys(SOUND_EFFECTS) as SoundEffectId[]).map((id) => {
          const effect = SOUND_EFFECTS[id];
          return NativeAudio.preload({
            assetId: effect.assetId,
            assetPath: this.#nativeAssetPath(effect.fileName),
            audioChannelNum: 1,
            isUrl: false,
            volume: effect.volume,
          });
        }),
      );
      return;
    }

    for (const id of Object.keys(SOUND_EFFECTS) as SoundEffectId[]) {
      const effect = SOUND_EFFECTS[id];
      const audio = new Audio(effect.webPath);
      audio.volume = effect.volume;
      audio.preload = 'auto';
      audio.load();
      this.#webAudio.set(id, audio);
    }
  }

  async #playEffect(id: SoundEffectId): Promise<void> {
    const effect = SOUND_EFFECTS[id];

    if (Capacitor.isNativePlatform()) {
      await NativeAudio.play({ assetId: effect.assetId });
      return;
    }

    const audio = this.#webAudio.get(id);
    if (!audio) {
      return;
    }

    audio.currentTime = 0;
    await audio.play();
  }

  /**
   * Platform asset locations (see @capacitor-community/native-audio README):
   * - iOS: `ios/App/App/sounds/<file>` (Xcode Resources → bundle root)
   * - Android: `android/app/src/main/assets/sounds/<file>`
   */
  #nativeAssetPath(fileName: string): string {
    switch (Capacitor.getPlatform()) {
      case 'ios':
        return fileName;
      case 'android':
        return `sounds/${fileName}`;
      default:
        return `assets/sounds/${fileName}`;
    }
  }
}
